import { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../../lib/auth-fetch';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Receipt, Save, Trash2, CheckCircle2, AlertCircle, Search, Filter, RefreshCw } from 'lucide-react';

export default function EasybillTab() {
  const [info, setInfo] = useState(null);
  const [apiKey, setApiKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState(null);
  const [testResult, setTestResult] = useState(null);

  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const load = useCallback(async () => {
    const res = await authFetch('/api/integrations/easybill/settings');
    if (res.ok) setInfo(await res.json());
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!apiKey) { setMessage({ type: 'error', text: 'API-Key darf nicht leer sein' }); return; }
    setSaving(true); setMessage(null);
    const res = await authFetch('/api/integrations/easybill/settings', {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ apiKey: apiKey.trim() }),
    });
    setSaving(false);
    if (res.ok) { setMessage({ type: 'success', text: 'API-Key gespeichert.' }); setApiKey(''); load(); }
    else { const d = await res.json().catch(() => ({})); setMessage({ type: 'error', text: d.detail || 'Fehler' }); }
  };

  const clear = async () => {
    if (!window.confirm('API-Key wirklich entfernen?')) return;
    const res = await authFetch('/api/integrations/easybill/settings', { method: 'DELETE' });
    if (res.ok) { setMessage({ type: 'success', text: 'API-Key entfernt.' }); load(); }
  };

  const toggleActive = async () => {
    const next = info?.active === false;
    const res = await authFetch('/api/integrations/easybill/settings/active', {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ active: next }),
    });
    if (res.ok) {
      setMessage({ type: 'success', text: next ? 'Easybill-Integration aktiviert.' : 'Easybill-Integration pausiert.' });
      load();
    } else {
      const d = await res.json().catch(() => ({}));
      setMessage({ type: 'error', text: d.detail || 'Fehler' });
    }
  };

  const test = async () => {
    setTesting(true); setTestResult(null);
    const res = await authFetch('/api/integrations/easybill/test');
    setTesting(false);
    if (res.ok) setTestResult({ ok: true, text: 'Verbindung erfolgreich.' });
    else {
      const d = await res.json().catch(() => ({}));
      setTestResult({ ok: false, text: d.detail || 'Verbindung fehlgeschlagen' });
    }
  };

  const search = async () => {
    if (!searchQ.trim()) return;
    setSearching(true);
    const res = await authFetch(`/api/integrations/easybill/customers?q=${encodeURIComponent(searchQ)}`);
    setSearching(false);
    if (res.ok) setSearchResults(await res.json());
    else { const d = await res.json().catch(() => ({})); setSearchResults({ items: [], error: d.detail }); }
  };

  return (
    <div data-testid="easybill-tab">
      <h2 className="text-sm font-semibold text-[#32373c] flex items-center gap-2 mb-3">
        <Receipt className="w-4 h-4 text-[#9ec043]" /> Easybill-Integration
      </h2>

      {message && (
        <div className={`rounded p-3 mb-4 text-sm border ${message.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-600'}`}>
          {message.text}
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-[#32373c]">API-Zugang</h3>
          <div className="flex items-center gap-2">
            {info?.configured ? (
              info?.active === false ? (
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-200 text-gray-700 flex items-center gap-1" data-testid="easybill-status">
                  Pausiert
                </span>
              ) : (
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-green-50 text-green-700 flex items-center gap-1" data-testid="easybill-status">
                  <CheckCircle2 className="w-3 h-3" /> Aktiv
                </span>
              )
            ) : (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 flex items-center gap-1" data-testid="easybill-status">
                <AlertCircle className="w-3 h-3" /> Noch kein Key
              </span>
            )}
            {info?.configured && (
              <button onClick={toggleActive} data-testid="btn-easybill-toggle-active"
                      className="text-[11px] text-[#9ec043] hover:underline">
                {info?.active === false ? 'Aktivieren' : 'Pausieren'}
              </button>
            )}
          </div>
        </div>

        <Label className="text-xs text-gray-500">{info?.configured ? 'Neuen API-Key eintragen (überschreibt vorhandenen)' : 'API-Key'}</Label>
        <Input type="password" value={apiKey} onChange={e => setApiKey(e.target.value)}
               placeholder="Easybill API-Key"
               className="mt-1 h-9 text-sm font-mono" data-testid="easybill-key" />
        <p className="text-[10px] text-gray-400 mt-1">
          Einen neuen Key erzeugst du unter <a href="https://my.easybill.de/pages/api" target="_blank" rel="noreferrer" className="text-[#9ec043] underline">my.easybill.de/pages/api</a>.
          Der Schlüssel wird Fernet-verschlüsselt gespeichert und nie im Klartext ausgeliefert.
        </p>

        <div className="flex gap-2 mt-3 flex-wrap">
          <Button onClick={save} disabled={saving || !apiKey}
                  className="bg-[#9ec043] hover:bg-[#8aad38] text-white h-9 text-sm" data-testid="btn-save-easybill">
            <Save className="w-3.5 h-3.5 mr-1" /> {saving ? 'Speichert…' : 'Speichern'}
          </Button>
          <Button onClick={test} disabled={testing || !info?.configured}
                  variant="outline" className="h-9 text-sm" data-testid="btn-test-easybill">
            {testing ? 'Teste…' : 'Verbindung testen'}
          </Button>
          {info?.configured && (
            <Button onClick={clear} variant="outline" className="h-9 text-sm text-red-600" data-testid="btn-clear-easybill">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> API-Key entfernen
            </Button>
          )}
        </div>
        {testResult && (
          <div className={`mt-3 rounded p-2 text-xs border ${testResult.ok ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-600'}`} data-testid="easybill-test-result">
            {testResult.text}
          </div>
        )}
      </div>

      {info?.configured && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-[#32373c] mb-3 flex items-center gap-2">
            <Search className="w-4 h-4 text-[#9ec043]" /> Kunden-Suche (Test)
          </h3>
          <div className="flex gap-2 mb-3">
            <Input value={searchQ} onChange={e => setSearchQ(e.target.value)}
                   onKeyDown={e => e.key === 'Enter' && search()}
                   placeholder="Name oder E-Mail"
                   className="h-9 text-sm" data-testid="easybill-search" />
            <Button onClick={search} disabled={searching || !searchQ.trim()}
                    className="bg-[#9ec043] hover:bg-[#8aad38] text-white h-9 text-sm" data-testid="btn-easybill-search">
              Suchen
            </Button>
          </div>
          {searchResults && (
            <div className="space-y-1">
              {searchResults.error && <p className="text-xs text-red-600">{searchResults.error}</p>}
              {searchResults.items?.length === 0 && <p className="text-xs text-gray-400">Keine Treffer.</p>}
              {(searchResults.items || []).map(c => (
                <div key={c.id} className="border-b border-gray-100 py-2 text-xs" data-testid={`eb-customer-${c.id}`}>
                  <div className="font-medium text-[#32373c]">{c.company_name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || 'Unbenannt'}</div>
                  <div className="text-gray-400">#{c.number} {(c.emails || [])[0] ? `· ${(c.emails || [])[0]}` : ''}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {info?.configured && <ArticleGroupWhitelistPanel />}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// Whitelist der Easybill-Artikelgruppen, aus denen Artikel für
// Angebote / Anfrage-Prefill importiert werden dürfen. Leere Auswahl
// = keine Einschränkung (alle Gruppen erlaubt). Nutzer kann z.B.
// alle (Alt)-Gruppen ausschließen, indem er nur die aktuellen wählt.
// ─────────────────────────────────────────────────────────────────
function ArticleGroupWhitelistPanel() {
  const [groups, setGroups] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [filterTxt, setFilterTxt] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const r = await authFetch('/api/integrations/easybill/article-groups');
    setLoading(false);
    if (r.ok) {
      const d = await r.json();
      setGroups(d.items || []);
      setSelected(new Set((d.items || []).filter(g => g.allowed).map(g => g.id)));
      const stale = (d.staleIds || []).length;
      if (stale > 0) {
        setMsg({ type: 'info',
                 text: `${stale} Gruppe(n) wurden in Easybill gelöscht und sind nicht mehr in der Whitelist enthalten — bitte „Whitelist speichern" klicken, um die Liste sauber zu halten.` });
      } else {
        setMsg(null);
      }
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setMsg({ type: 'info', text: 'Lade aktuelle Gruppen aus Easybill…' });
    await load();
  };

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelected(new Set(filteredGroups.map(g => g.id)));
  const clear = () => setSelected(new Set());
  const selectAllNonAlt = () => setSelected(new Set(
    groups.filter(g => !/\(Alt\)\s*$/i.test(g.name || '')).map(g => g.id)
  ));

  const save = async () => {
    setSaving(true); setMsg(null);
    const r = await authFetch('/api/integrations/easybill/settings/article-groups', {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids: Array.from(selected) }),
    });
    setSaving(false);
    if (r.ok) {
      const d = await r.json();
      setMsg({ type: 'success',
               text: d.count > 0
                 ? `Whitelist gespeichert — ${d.count} Gruppen erlaubt.`
                 : 'Whitelist geleert — alle Gruppen erlaubt.' });
      load();
    } else {
      const d = await r.json().catch(() => ({}));
      setMsg({ type: 'error', text: d.detail || 'Fehler' });
    }
  };

  const filteredGroups = filterTxt
    ? groups.filter(g => (g.name || '').toLowerCase().includes(filterTxt.toLowerCase()))
    : groups;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="article-groups-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h3 className="text-sm font-semibold text-[#32373c] flex items-center gap-2">
          <Filter className="w-4 h-4 text-[#9ec043]" /> Artikelgruppen-Whitelist
        </h3>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={refresh}
                  disabled={loading} className="h-7 text-[11px]"
                  title="Liste aktueller Easybill-Gruppen neu laden — z.B. nach Änderungen oder Löschungen in Easybill."
                  data-testid="btn-refresh-article-groups">
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
          <span className="text-[11px] text-gray-400">
            {selected.size === 0
              ? 'Keine Einschränkung'
              : `${selected.size} Gruppen erlaubt`}
          </span>
        </div>
      </div>
      <p className="text-[11px] text-gray-500 mb-3">
        Wähle die Easybill-Artikelgruppen, aus denen das EES-System Artikel für
        Angebots-Prefill importieren darf. Leere Auswahl = alle Gruppen.
        Tipp: Klicke „Nur Aktuelle (ohne Alt)", um Gruppen mit „(Alt)"-Suffix
        einmalig auszuschließen.
      </p>

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Input value={filterTxt} onChange={e => setFilterTxt(e.target.value)}
               placeholder="Gruppen filtern…"
               className="h-7 text-xs flex-1 min-w-[180px]"
               data-testid="article-groups-filter" />
        <Button size="sm" variant="outline" onClick={selectAllNonAlt}
                className="h-7 text-[11px]" data-testid="btn-select-non-alt">
          Nur Aktuelle (ohne Alt)
        </Button>
        <Button size="sm" variant="outline" onClick={selectAll}
                className="h-7 text-[11px]">Alle</Button>
        <Button size="sm" variant="outline" onClick={clear}
                className="h-7 text-[11px]">Keine</Button>
      </div>

      <div className="border border-gray-100 rounded-lg max-h-[320px] overflow-y-auto">
        {loading ? (
          <div className="p-4 text-xs text-gray-400">Lade Gruppen…</div>
        ) : filteredGroups.length === 0 ? (
          <div className="p-4 text-xs text-gray-400">Keine Gruppen gefunden.</div>
        ) : filteredGroups.map(g => {
          const isAlt = /\(Alt\)\s*$/i.test(g.name || '');
          const checked = selected.has(g.id);
          return (
            <label key={g.id}
                   className={`flex items-center gap-2 px-3 py-1.5 text-xs border-b border-gray-50 last:border-b-0 cursor-pointer hover:bg-gray-50 ${
                     isAlt ? 'bg-amber-50/30' : ''
                   }`}
                   data-testid={`ag-row-${g.id}`}>
              <input type="checkbox" checked={checked}
                     onChange={() => toggle(g.id)}
                     data-testid={`ag-cb-${g.id}`}
                     className="accent-[#9ec043]" />
              <span className="flex-1">{g.name}</span>
              {isAlt && (
                <span className="px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wide bg-amber-100 text-amber-700">
                  Alt
                </span>
              )}
              <span className="text-gray-300 text-[10px]">#{g.id}</span>
            </label>
          );
        })}
      </div>

      <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
        {msg && (
          <span className={`text-[11px] ${
            msg.type === 'success' ? 'text-green-600' :
            msg.type === 'error'   ? 'text-red-600'  :
                                     'text-gray-500'
          }`}
                data-testid="ag-save-msg">{msg.text}</span>
        )}
        <Button size="sm" onClick={save} disabled={saving}
                className="bg-[#9ec043] hover:bg-[#8aad38] text-white h-8 text-xs ml-auto"
                data-testid="btn-save-article-groups">
          <Save className="w-3 h-3 mr-1" />
          {saving ? 'Speichert…' : 'Whitelist speichern'}
        </Button>
      </div>
    </div>
  );
}
